// Define the application's config state to get PARTNER_TOKEN (assuming it's in some global config or context)
import { userSession } from '../../state/session';
import { ApiError } from 'utils/errors';
import { EErrorCode } from 'config/errors-codes';
import { user } from 'utils/auth/user';
import { showErrorToast } from 'utils/ui/errors';
import { recordStaleWrite } from 'state/session/syncTelemetry';

export type TMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

// Define the type for request options
export type RequestOptions = Omit<RequestInit, 'headers' | 'body'> & {
  headers?: Record<string, string>;
  method?: TMethod;
  body?: unknown;
  origin?: string;
  idToken?: boolean;
  suppressToast?: boolean; // Caller owns error reporting — skip both the global error toast and the wrapper's console.error
  // Surface a SESSION_STALE_WRITE 409 as a typed throw instead of a silent undefined, so a
  // compare-and-set caller can rebase on the server state and retry (AVS-4330).
  throwOnStaleWrite?: boolean;
  // Narrows the duplicate-request guard: only requests with the same method, URL and key abort each other (AVS-4445).
  dedupeKey?: string;
};

// Reusable function to determine the API base URL
export const getApiBaseUrl = (): string => {
  if (process.env.API_URL) {
    return process.env.API_URL;
  }
  throw new Error('API_URL is not defined.');
};

// Map to track active requests by their unique identifier (method + URL)
const activeRequests = new Map<string, AbortController>();

// Helper function to create a unique request identifier
const getRequestId = (method: string, url: string, dedupeKey?: string): string => {
  return dedupeKey ? `${method}:${url}#${dedupeKey}` : `${method}:${url}`;
};

// Function to cancel a specific request if it exists
const cancelRequest = (requestId: string, reason: string = 'Duplicate request'): boolean => {
  const controller = activeRequests.get(requestId);
  if (controller) {
    controller.abort(reason);
    activeRequests.delete(requestId);
    return true;
  }
  return false;
};

/* -------------------------------------------------------------------------------------------------
 * Session Flight Recorder seam — AVS-4432 (parent AVS-4423)
 *
 * `sendApiRequest` is the single chokepoint for API traffic, which makes it the only place where a
 * request's METHOD, URL, BODY, STATUS, RESPONSE BODY and DURATION are all in scope at once. The two
 * branches the Story names are here and nowhere else: the duplicate-request abort (`:87-93` below)
 * and the 409 stale-write discard (`:127-140`). Both make a session write vanish without a trace —
 * on 2026-09-04 the whole analysis had to be reconstructed from three screenshots because of it.
 *
 * ## Why a settable sink and not an import of `telemetry/recorder`
 *
 * `scripts/check-recorder-gate.mjs` proves by grep that no prod or `STAGE=''` artefact contains
 * recorder code, and it greps for a sentinel that lives in `telemetry/recorder/core.ts`. This module
 * is in EVERY bundle, client and lambda alike, so a static import of the core here would put that
 * sentinel in a prod build and break the gate. The sink is therefore the same discipline as
 * `setListenerGuardReporter` (`utils/events.ts:61`) and `setLogSink` (`utils/log.ts:34`): a
 * module-level settable function, defaulting to none, called from inside this module and wrapped in
 * its own try/catch so a throwing sink can never fail a request.
 *
 * ## What is deliberately NOT here
 *
 * Request headers never leave this function. `Authorization`, `Partner-Token`, `X-Id-Token` and
 * `Context-Token` are credentials in transit, so the sink is handed their PRESENCE
 * ({@link IApiCredentialPresence}) and the NAMES of any caller-supplied extras, never a value. The
 * one token that does travel is `partnerToken`, which is a tenant identifier with a schema default
 * (`state/session.ts:69`) and not a secret — losing it costs tenant context in every recording.
 * ---------------------------------------------------------------------------------------------- */

/**
 * Which point in a request's life an {@link IApiSinkEvent} describes.
 *
 * `duplicate-abort` and `stale-write` are branches, not failures: both return `undefined` to a
 * caller that asked for a write, so in a recording they are the difference between "the write was
 * refused" and "the write is missing and nobody knows why".
 */
export type TApiPhase = 'request' | 'response' | 'error' | 'stale-write' | 'duplicate-abort';

/** Header PRESENCE, never header values. See "What is deliberately NOT here" above. */
export interface IApiCredentialPresence {
  /** An `Authorization: Bearer …` header was attached. */
  auth: boolean;
  /** An `X-Id-Token` header was attached. */
  idToken: boolean;
  /** A `Context-Token` header was attached. */
  contextToken: boolean;
}

/**
 * One observation of an API request.
 *
 * Every field is either metadata or a payload the caller already owns. `body` and `response` are
 * passed LIVE and must not be pre-copied — the recorder's redactor snapshots them synchronously
 * (`telemetry/recorder/core.ts` constraint 4), and a pre-serialization here would both cost the
 * request path and defeat the redaction.
 */
export interface IApiSinkEvent {
  phase: TApiPhase;
  /** Per-tab monotonic id, shared by every phase of one request. */
  id: number;
  method: TMethod;
  /** Fully-resolved URL, query included — the session PUT's CAS semantics live in its query. */
  url: string;
  /** The endpoint as the caller wrote it, so a route is greppable without parsing the URL. */
  endpoint: string;
  /** `method:url`, the key the duplicate-request map is built on. */
  requestId: string;
  credentials: IApiCredentialPresence;
  /** Names only, of headers the caller supplied. Values are never recorded. */
  extraHeaderNames?: string[];
  /**
   * The tenant identifier, exactly as the `Partner-Token` header carried it — including the literal
   * `'undefined'` this wrapper produces when the session has none, which is itself evidence.
   *
   * Allow-listed by the recorder's redactor, deliberately: it has a schema default
   * (`state/session.ts:69`) and is not a secret (AVS-4423).
   */
  partnerToken?: string;
  suppressToast?: boolean;
  throwOnStaleWrite?: boolean;
  keepalive?: boolean;
  /** The LIVE request body, unserialized. Present on `request` only. */
  body?: unknown;
  status?: number;
  ok?: boolean;
  /** The parsed response body, or the parsed error body on a non-2xx. */
  response?: unknown;
  /** Wall time from just before `fetch` to this phase, in ms. */
  durationMs?: number;
  errorCode?: EErrorCode;
  errorMessage?: string;
}

/** Registered by `telemetry/recorder/taps/apiTap.ts`. Must never throw; it is isolated anyway. */
export type TApiSink = (event: IApiSinkEvent) => void;

let apiSink: TApiSink | null = null;

let apiTraceSeq = 0;

/**
 * Register the recorder's API sink. Pass `null` to remove it.
 *
 * A suite that installs one restores it in its own `afterEach` — see
 * `utils/network/apiRecorderSink.test.ts`.
 */
export function setApiRequestSink(sink: TApiSink | null): void {
  apiSink = sink;
}

/** Whether an API sink is currently registered. The one boolean a recorder-less build pays. */
function isApiTraced(): boolean {
  return apiSink !== null;
}

/** The sink is registered from outside and arbitrary — a throwing one must never fail a request. */
function reportApi(event: IApiSinkEvent): void {
  const sink = apiSink;

  if (!sink) {
    return;
  }

  try {
    sink(event);
  } catch (error) {
    console.error('[API] recorder sink threw and was isolated', error);
  }
}

/**
 * Depth of `sendApiRequest`'s own, synchronous `fetch` invocation.
 *
 * A counter rather than a boolean because `sendApiRequest` is re-entrant across requests; it is
 * incremented and decremented around the CALL, never across the `await`, so it is true for exactly
 * the synchronous entry into `fetch` that this wrapper made.
 */
let wrapperFetchDepth = 0;

/**
 * True while `sendApiRequest` is synchronously invoking `fetch`.
 *
 * Read by `apiTap`'s `globalThis.fetch` wrapper (AVS-4432) so one request is not recorded twice —
 * once with full payloads on the `api` lane and again as bare metadata on the `net` lane. The `net`
 * lane exists for everything this wrapper does NOT own: `utils/network/legal.ts`, `utils/auth/*`,
 * `utils/ui/downloadImage.ts` and the rest.
 */
export function isApiWrapperFetch(): boolean {
  return wrapperFetchDepth > 0;
}

/**
 * Invoke `fetch` with {@link isApiWrapperFetch} set for the duration of the CALL.
 *
 * The flag must not span the `await`: everything else the tab fetches while this request is in
 * flight would then be attributed to it and dropped from the `net` lane.
 */
function taggedFetch(url: string, init: RequestInit): Promise<Response> {
  wrapperFetchDepth += 1;

  try {
    return fetch(url, init);
  } finally {
    wrapperFetchDepth -= 1;
  }
}

// Reusable function for making API calls with error handling and header setup
export const sendApiRequest = async <T>(
  endpoint: string,
  options: RequestOptions = {},
  auth = true,
  contextToken?: string
): Promise<T | undefined> => {
  const PARTNER_TOKEN = String(userSession().get('partnerToken'));
  const AUTH_TOKEN = user.getAccessToken();
  const ID_TOKEN = user.getIdToken();

  const apiUrl = options.origin ?? getApiBaseUrl();
  const url = `${apiUrl}${endpoint}`;
  const method = options.method || 'GET';

  // Create a unique identifier for this request
  const requestId = getRequestId(method, url, options.dedupeKey);

  // AVS-4432 recorder seam. Built once per request and only when a sink is registered, so a build
  // without the recorder pays a single boolean read and nothing else.
  const trace: Omit<IApiSinkEvent, 'phase'> | undefined = isApiTraced()
    ? {
        id: apiTraceSeq++,
        method,
        url,
        endpoint,
        requestId,
        credentials: {
          auth: Boolean(auth && AUTH_TOKEN),
          idToken: Boolean(auth && options.idToken && ID_TOKEN),
          contextToken: Boolean(contextToken),
        },
        extraHeaderNames: options.headers ? Object.keys(options.headers) : undefined,
        partnerToken: PARTNER_TOKEN,
        suppressToast: options.suppressToast,
        throwOnStaleWrite: options.throwOnStaleWrite,
        keepalive: options.keepalive,
      }
    : undefined;
  const startedAt = performance.now();
  let responded = false;

  // Check if there's already an identical request in progress
  if (activeRequests.has(requestId)) {
    if (import.meta?.env?.MODE === 'development') {
      console.log(`Cancelling previous ${method} request to ${url}`);
    }
    // AVS-4432: recorded HERE, synchronously, rather than inferred from the victim's rejection. The
    // aborted request rejects with the bare string `'Duplicate request'` into the branch at the
    // bottom of this function, which logs and resolves to `undefined` — on a PUT/POST/DELETE that is
    // a write that silently vanished. The victim's own `error` phase follows; this is the event that
    // says who killed it.
    if (trace) {
      reportApi({ ...trace, phase: 'duplicate-abort' });
    }
    // Cancel the previous request to the same endpoint
    cancelRequest(requestId);
  }

  // Setup headers with PARTNER_TOKEN
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Partner-Token': PARTNER_TOKEN,
    ...(options.headers || {}), // Include any additional headers passed in options
    ...(auth && AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {}),
    ...(auth && options.idToken && ID_TOKEN ? { 'X-Id-Token': ID_TOKEN } : {}),
    ...(contextToken ? { 'Context-Token': `Bearer ${contextToken}` } : {}),
  };

  // Create an AbortController for this request
  const controller = new AbortController();
  const signal = controller.signal;

  // Register this request
  activeRequests.set(requestId, controller);

  // The LIVE body, never a pre-serialized copy: the recorder's redactor snapshots it synchronously
  // and a `JSON.stringify` here would cost the request path and hand the redactor an opaque string.
  if (trace) {
    reportApi({ ...trace, phase: 'request', body: options.body });
  }

  try {
    const response = await taggedFetch(url, {
      ...options,
      headers,
      method,
      body: options.body ? JSON.stringify(options.body) : undefined, // Serialize the body if present
      signal, // Attach the signal for aborting requests
    });

    if (!response.ok) {
      // Error handling: Try to extract a JSON error message from the response
      const errorData = await response.json().catch(() => null);

      // Stale write: a typed throw for CAS callers (they rebase + retry), a silent discard for
      // everyone else (the WebSocket broadcast delivers current state).
      if (response.status === 409 && errorData?.code === EErrorCode.SESSION_STALE_WRITE) {
        // Counted here, above the branch, so the number is transport-level and does not depend on
        // whether the caller opted into the typed throw (AVS-4332). Note for anyone reading the
        // number: state/session retries once after a conflict, so one logical conflict can record
        // two stale writes — this is a stale-write rate, not a conflict rate.
        recordStaleWrite();
        // AVS-4432: the branch where a session write is refused. Without `throwOnStaleWrite` the
        // caller sees `undefined` and cannot tell a rejected write from a 200 with no body, which is
        // precisely the "Session update failed" ambiguity this recording exists to resolve.
        if (trace) {
          responded = true;
          reportApi({
            ...trace,
            phase: 'stale-write',
            status: response.status,
            ok: false,
            response: errorData,
            durationMs: performance.now() - startedAt,
            errorCode: EErrorCode.SESSION_STALE_WRITE,
          });
        }
        if (options.throwOnStaleWrite) {
          throw new ApiError(EErrorCode.SESSION_STALE_WRITE, 'Stale write rejected', {
            statusCode: 409,
            silent: true,
          });
        }
        return undefined;
      }

      const errorMessage = errorData?.error || response.statusText;
      const errorCode: EErrorCode = errorData?.code || EErrorCode.INTERNAL_ERROR;
      if (trace) {
        responded = true;
        reportApi({
          ...trace,
          phase: 'response',
          status: response.status,
          ok: false,
          response: errorData,
          durationMs: performance.now() - startedAt,
          errorCode,
          errorMessage,
        });
      }
      throw new ApiError(errorCode, `Error: ${response.status} - ${errorMessage}`, {
        statusCode: response.status,
        silent: options.suppressToast,
      });
    }

    // Parse the JSON response
    const data = await response.json();
    if (trace) {
      responded = true;
      reportApi({
        ...trace,
        phase: 'response',
        status: response.status,
        ok: true,
        response: data,
        durationMs: performance.now() - startedAt,
      });
    }
    return data as T;
  } catch (error) {
    // Only when no `response`/`stale-write` phase already described this request. An HTTP error is
    // fully described by its response event; what reaches here otherwise is a transport failure, an
    // abort, or a body that would not parse — outcomes with no response event at all.
    if (trace && !responded) {
      reportApi({
        ...trace,
        phase: 'error',
        durationMs: performance.now() - startedAt,
        errorCode: error instanceof ApiError ? error.code : undefined,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }
    if (error === 'Duplicate request') {
      console.warn(`Duplicate request aborted: ${method} ${url}`, error);
    } else {
      // A stale-write signal (only minted above, under throwOnStaleWrite) is expected control
      // flow for the CAS caller — no console noise, no toast, just the typed rethrow.
      const isStaleWriteSignal = error instanceof ApiError && error.code === EErrorCode.SESSION_STALE_WRITE;

      // suppressToast signals the caller owns error reporting — stay silent in the wrapper.
      if (!options.suppressToast && !isStaleWriteSignal) {
        console.error('API request failed:', error);
      }

      if (error instanceof ApiError) {
        if (!options.suppressToast && !isStaleWriteSignal) {
          showErrorToast(error.code);
        }
        throw error;
      }

      throw new ApiError(EErrorCode.INTERNAL_ERROR, (error as Error).message, {
        silent: options.suppressToast,
      });
    }
  } finally {
    // Cleanup: Remove this request from tracking when done
    activeRequests.delete(requestId);
  }
};
